// src/hooks/useAdminDashboard.js

import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { companyService } from '../services/companyService';
import {
  getCompanyData,
  initializeDataLayer,
  subscribeToAssets,
  subscribeToUsers,
  loadDashboardReferenceData,
} from '../services/adminDashboardService';
import {
  EMPTY_METRICS,
  processAssetsData,
} from '../utils/assetProcessing';

// ─── Session Cache ────────────────────────────────────────────────────────────
const sessionDashboardCache = new Map();

export const clearDashboardCache = (cacheKey = null) => {
  if (cacheKey) sessionDashboardCache.delete(cacheKey);
  else sessionDashboardCache.clear();
};

const getCacheKey = (user) => {
  if (!user?.companyId) return null;
  return `${user.companyId}::${user.role || 'admin'}::${user.locationCode || 'all'}`;
};

// ─── Hook Definition ──────────────────────────────────────────────────────────
export const useAdminDashboard = (showDashboard) => {
  const { currentUser, loading: authLoading } = useAuth();

  // Robust userId fallback (handles uid, id, or userId)
  const userId = currentUser?.uid || currentUser?.id || currentUser?.userId;

  const cacheKey = getCacheKey(currentUser);
  const rawCached = cacheKey ? sessionDashboardCache.get(cacheKey) : null;
  const cached = rawCached?.__cacheVersion === 2 ? rawCached : null;

  const [loading, setLoading] = useState(!cached);
  const [initProgress, setInitProgress] = useState(0);
  const [initProgressLabel, setInitProgressLabel] = useState('');
  const [companyData, setCompanyData] = useState(null);
  const [companyError, setCompanyError] = useState(null);
  const [dashboardData, setDashboardData] = useState(
    cached?.dashboardData ?? { ...EMPTY_METRICS }
  );

  const [assetsByCategory, setAssetsByCategory] = useState(cached?.assetsByCategory ?? []);
  const [assetsBySubCategory, setAssetsBySubCategory] = useState(cached?.assetsBySubCategory ?? []);
  const [assetsByBrand, setAssetsByBrand] = useState(cached?.assetsByBrand ?? []);
  const [assetsByLocation, setAssetsByLocation] = useState(cached?.assetsByLocation ?? []);
  const [assetsByStatus, setAssetsByStatus] = useState(cached?.assetsByStatus ?? []);
  const [monthlyAssetTrend, setMonthlyAssetTrend] = useState(cached?.monthlyAssetTrend ?? []);
  const [recentActivities, setRecentActivities] = useState(cached?.recentActivities ?? []);

  const latestAssetsRef = useRef(cached?.rawAssets ?? []);
  const latestUsersCountRef = useRef(cached?.dashboardData?.usersCount ?? 0);
  const isMountedRef = useRef(true);
  const lastAssetsFingerprintRef = useRef(cached?.assetsFingerprint ?? '');

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // ─── Wipe on logout ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!userId) {
      setDashboardData({ ...EMPTY_METRICS });
      setAssetsByCategory([]);
      setAssetsBySubCategory([]);
      setAssetsByBrand([]);
      setAssetsByLocation([]);
      setAssetsByStatus([]);
      setMonthlyAssetTrend([]);
      setRecentActivities([]);
      setCompanyData(null);
      setCompanyError(null);
      setLoading(true);
      lastAssetsFingerprintRef.current = '';
      latestAssetsRef.current = [];
      latestUsersCountRef.current = 0;
    }
  }, [userId]);

  // ─── Fetch Company Data from Firestore ─────────────────────────────────────
  useEffect(() => {
    if (authLoading || !userId) return;

    let isMounted = true;

    const fetchCompany = async () => {
      const companyId = currentUser?.companyId;

      if (!companyId) {
        console.warn('[useAdminDashboard] No companyId on currentUser');
        if (isMounted) {
          setCompanyError('No companyId associated with this account.');
          setCompanyData(null);
        }
        return;
      }

      // 1. Try localStorage first (fast path)
      let localData = null;
      try {
        localData = getCompanyData();
      } catch (e) {
        console.warn('[useAdminDashboard] getCompanyData() failed:', e);
      }

      if (localData && (localData.id === companyId || localData.companyId === companyId)) {
        console.log('[useAdminDashboard] Using cached company from localStorage:', localData);
        if (isMounted) {
          setCompanyData(localData);
          setCompanyError(null);
        }
        return;
      }

      // 2. Fallback: Fetch directly from Firestore
      console.log('[useAdminDashboard] Fetching company from Firestore:', companyId);
      try {
        const fetchCompanyFn = companyService.getCompanyById || companyService.fetchCompanyById;
        let company = null;
        if (typeof fetchCompanyFn === 'function') {
          company = await fetchCompanyFn.call(companyService, companyId);
        }

        if (!company) {
          console.error(`[useAdminDashboard] Company "${companyId}" not found in Firestore.`);
          if (isMounted) {
            setCompanyError(`Company "${companyId}" not found in database.`);
            setCompanyData(null);
          }
          return;
        }

        console.log('[useAdminDashboard] Fetched company:', company);

        if (typeof window !== 'undefined') {
          localStorage.setItem('companyData', JSON.stringify(company));
        }

        if (isMounted) {
          setCompanyData(company);
          setCompanyError(null);
        }
      } catch (err) {
        console.error('[useAdminDashboard] Error fetching company:', err);
        if (isMounted) {
          setCompanyError(err.message || 'Failed to load company data.');
          setCompanyData(null);
        }
      }
    };

    fetchCompany();

    return () => {
      isMounted = false;
    };
  }, [userId, authLoading, currentUser?.companyId]);

  // ─── Apply Processed Snapshot ──────────────────────────────────────────────
  const applyProcessed = (processed, usersCount, rawAssets, fingerprint) => {
    const mergedMetrics = {
      ...processed.metrics,
      usersCount,
    };

    const total = processed.metrics?.totalAssets || 0;
    const unassigned = processed.metrics?.unassignedAssets || 0;
    const assigned = Math.max(0, total - unassigned);
    const statusData = [
      { status: 'Available', count: processed.metrics?.activeAssets || 0 },
      { status: 'Assigned', count: assigned },
      { status: 'Unassigned', count: unassigned },
      { status: 'In Repair', count: processed.metrics?.inRepairAssets || 0 },
      { status: 'Disposed', count: processed.metrics?.disposedAssets || 0 },
    ].filter((item) => item.count > 0 || total === 0);

    const snapshot = {
      dashboardData: mergedMetrics,
      assetsByCategory: processed.categoryData || [],
      assetsBySubCategory: processed.subCategoryData || [],
      assetsByBrand: processed.brandData || [],
      assetsByLocation: processed.locationData || [],
      assetsByStatus: statusData,
      monthlyAssetTrend: processed.monthlyTrend || [],
      recentActivities: processed.activities || [],
      rawAssets: rawAssets || [],
      assetsFingerprint: fingerprint || '',
    };

    if (cacheKey) {
      sessionDashboardCache.set(cacheKey, { ...snapshot, __cacheVersion: 2 });
    }

    if (!isMountedRef.current) return;

    setDashboardData(mergedMetrics);
    setAssetsByCategory(snapshot.assetsByCategory);
    setAssetsBySubCategory(snapshot.assetsBySubCategory);
    setAssetsByBrand(snapshot.assetsByBrand);
    setAssetsByLocation(snapshot.assetsByLocation);
    setAssetsByStatus(snapshot.assetsByStatus);
    setMonthlyAssetTrend(snapshot.monthlyAssetTrend);
    setRecentActivities(snapshot.recentActivities);
    setLoading(false);
  };

  const fingerprintAssets = (assets = []) => {
    return assets
      .map((a) => `${a?.id || a?.assetId || ''}:${a?.updatedAt || a?.status || ''}:${a?.assignedTo || ''}`)
      .join('|');
  };

  // ─── Subscriptions Pipeline (SINGLE useEffect) ────────────────────────────
  useEffect(() => {
    if (authLoading || !userId || !showDashboard || !cacheKey) {
      if (!authLoading) setLoading(false);
      return;
    }

    const companyId = currentUser?.companyId;
    if (!companyId) {
      console.warn('[useAdminDashboard] currentUser missing companyId');
      setLoading(false);
      return;
    }

    const activeCache = sessionDashboardCache.get(cacheKey);
    if (!activeCache) {
      setLoading(true);
    }

    // ── reference-data lookup maps ──
    const categoriesMap    = new Map();
    const subCategoriesMap = new Map();
    const brandsMap        = new Map();
    const locationsMap     = new Map();
    let refDataReady = false;
    let pendingAssets = null;

    const tryProcessAssets = (assets) => {
      if (!isMountedRef.current) return;
      if (!refDataReady) { pendingAssets = assets; return; }

      const fp = fingerprintAssets(assets);
      if (fp && fp === lastAssetsFingerprintRef.current && sessionDashboardCache.has(cacheKey)) {
        setLoading(false);
        return;
      }

      lastAssetsFingerprintRef.current = fp;
      latestAssetsRef.current = assets;

      const processed = processAssetsData(assets, {
        categoriesMap,
        subCategoriesMap,
        brandsMap,
        locationsMap,
      });
      applyProcessed(processed, latestUsersCountRef.current, assets, fp);
    };

  // ── load categories / subcategories / brands / locations (delegated to service) ──
const loadReferenceData = async () => {
  try {
    const { categories, subcategories, brands, locations } =
      await loadDashboardReferenceData(companyId);

    categories.forEach((c) =>
      categoriesMap.set(c.id || c.categoryId, c)
    );
    subcategories.forEach((s) =>
      subCategoriesMap.set(s.id || s.subCategoryId, s)
    );
    brands.forEach((b) =>
      brandsMap.set(b.id || b.brandId, b)
    );
    locations.forEach((l) =>
      locationsMap.set(l.id || l.locationCode, l)
    );
  } catch (err) {
    console.error('[useAdminDashboard] Reference data load failed:', err);
  } finally {
    refDataReady = true;
    if (pendingAssets) { tryProcessAssets(pendingAssets); pendingAssets = null; }
  }
};

    loadReferenceData();

    const unsubscribeAssets = subscribeToAssets(
      companyId,
      currentUser.role,
      currentUser.locationCode,
      (assets) => tryProcessAssets(assets),
      (err) => {
        console.error('[useAdminDashboard] Assets stream error:', err);
        if (isMountedRef.current) setLoading(false);
      }
    );

    const unsubscribeUsers = subscribeToUsers(
      companyId,
      (users) => {
        if (!isMountedRef.current) return;
        const usersCount = Array.isArray(users) ? users.length : 0;
        latestUsersCountRef.current = usersCount;

        setDashboardData((prev) => {
          const merged = { ...prev, usersCount };
          const existing = sessionDashboardCache.get(cacheKey);
          if (existing) {
            sessionDashboardCache.set(cacheKey, {
              ...existing,
              dashboardData: { ...existing.dashboardData, usersCount },
            });
          }
          return merged;
        });
      },
      (err) => {
        console.error('[useAdminDashboard] Users stream error:', err);
      }
    );

    initializeDataLayer(
      (pct, label) => {
        if (!isMountedRef.current) return;
        setInitProgress(pct);
        setInitProgressLabel(label);
      },
      (errReport) => {
        console.warn('[useAdminDashboard] Probe roadblock (non-fatal):', errReport.phase);
      }
    ).catch((err) => {
      console.warn('[useAdminDashboard] Probe failed (non-fatal):', err?.message);
    });

    return () => {
      if (typeof unsubscribeAssets === 'function') unsubscribeAssets();
      if (typeof unsubscribeUsers === 'function') unsubscribeUsers();
    };
  }, [userId, authLoading, showDashboard, cacheKey, currentUser]);

  return {
    loading,
    initProgress,
    initProgressLabel,
    companyData,
    companyError,
    dashboardData,
    assetsByCategory,
    assetsBySubCategory,
    assetsByBrand,
    assetsByLocation,
    assetsByStatus,
    monthlyAssetTrend,
    recentActivities,
    refreshDashboard: () => clearDashboardCache(cacheKey),
  };
};

export default useAdminDashboard;